import { useTheme } from "@emotion/react";
import { Box, Grid, Typography } from "@mui/material";
import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { createData } from "shared-components/src/components/displayData/CustomizedTables.component";
import FixedSizeListComponent from "shared-components/src/components/displayData/FixedSizeList.component";
import MapComponent from "shared-components/src/components/map/map.component";

const CompactEmptyState = ({ title, description }) => (
  <Box
    border="1px dashed"
    borderColor="grey.300"
    p={2}
    borderRadius={2}
    mb={2}
    textAlign="center"
    bgcolor="grey.50"
  >
    <Typography fontWeight="bold" color="text.secondary" gutterBottom>
      {title}
    </Typography>
    <Typography variant="body2" color="text.secondary" mb={1}>
      {description}
    </Typography>
  </Box>
);

const MapFieldComponent = ({ isRealtime }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const fieldSelected = useSelector((state) => state.field.data);
  const fields = useSelector((state) => state.fields.data);
  const field = fields.find((f) => f.id === fieldSelected);
  const polygons = field?.polygons ?? [];
  const sensors = field?.sensors ?? [];
  const sensors_data = field?.realtime?.sensors_data ?? [];
  const weatherStation = field?.weather_station?.label;

  const setSensorList = (sensors) => {
    return !sensors
      ? []
      : sensors.map((sensor, index) => index + 1 + ". " + sensor.label);
  };

  const sensorsList = setSensorList(sensors);

  const setSensorDataRealtime = (sensor_id, sensors_data) => {
    const data = sensors_data?.find((sensor) => sensor_id === sensor.id)?.data;
    return data ? data.map((d) => createData(d.label, d.value)) : null;
  };

  const setMarkersWithNumber = (sensors, sensors_data) => {
    return !sensors
      ? []
      : sensors
          .filter((sensor) => {
            return (
              sensor.position &&
              Array.isArray(sensor.position) &&
              sensor.position.length === 2 &&
              !isNaN(sensor.position[0]) &&
              !isNaN(sensor.position[1])
            );
          })
          .map((sensor, index) => {
            let popup = null;
            let popupWithTable = null;

            if (setSensorDataRealtime(sensor.id, sensors_data)) {
              popupWithTable = {
                rows: setSensorDataRealtime(sensor.id, sensors_data),
                tableTitle: sensor.label,
              };
            } else {
              popup = `<div>${sensor.label}</div>`;
            }

            return {
              id: sensor.id,
              position: sensor.position,
              iconNumber: index + 1,
              color: theme.palette.marker.main,
              popup: popup,
              popupWithTable: popupWithTable,
            };
          });
  };

  const markersWithNumber = setMarkersWithNumber(sensors, sensors_data);

  return (
    <Fragment>
      <Grid container rowSpacing={2} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
        <Grid item xs={12} sm={9} md={isRealtime ? 12 : 9}>
          <MapComponent
            polygons={polygons}
            markersWithNumber={markersWithNumber}
          ></MapComponent>
        </Grid>
        {!isRealtime && (
          <Grid item xs={12} sm={6} md={3} sx={{ paddingTop: 2 }}>
            {sensorsList.length !== 0 ? (
              <>
                <Typography fontWeight="bold">
                  {t("map_field.title")}
                </Typography>
                <FixedSizeListComponent rows={sensorsList} />
              </>
            ) : (
              <CompactEmptyState
                title={t("map_field.no_sensors_title")}
                description={t("map_field.no_sensors_description")}
              />
            )}

            {weatherStation ? (
              <>
                <Typography fontWeight="bold" sx={{ mb: 1 }}>
                  {t("map_field.weather_station")}
                </Typography>
                <Typography variant="body1">- {weatherStation}</Typography>
              </>
            ) : (
              <CompactEmptyState
                title={t("map_field.no_ws_title")}
                description={t("map_field.no_ws_description")}
              />
            )}
          </Grid>
        )}
      </Grid>
    </Fragment>
  );
};

export default MapFieldComponent;
